import React, { useState, useEffect } from 'react';
import localMenuData from '../menuData.json'; // Import local JSON data
import Modal from '../components/Modal';
import MenuCategoryCard from '../components/MenuCategoryCard';

const MenuPage = ({ navigateTo }) => {
  const [activeMenuSection, setActiveMenuSection] = useState('food');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [menuData, setMenuData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMenu = async () => {
      try {
        const response = await fetch("https://grandeplatform.eu.pythonanywhere.com/api/menu_items");
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setMenuData(data);
      } catch (error) {
        // If API fetch fails, load local data
        console.error("Failed to fetch menu from API, falling back to local data:", error);
        setError(error); // Still set error to indicate API failure
        setMenuData(localMenuData); // Load local data
      } finally {
        setLoading(false);
      }
    };

    fetchMenu();
  }, []); // Empty dependency array means this effect runs once on mount

  // --- Use the fetched menu data ---
  const foodMenu = menuData;
  const drinksMenu = menuData;

  const openCategoryModal = (categoryName) => {
    setSelectedCategory(categoryName);
  };

  const closeCategoryModal = () => {
    setSelectedCategory(null);
  };

  // Use the fetched menu data directly
  const allMenuData = menuData;

  // --- Rendering helpers ---
  // Helper to format price consistently
  const formatPrice = (p) => typeof p === 'number' ? `${p}:-` : p;

   // --- RENDER MENU ITEM HELPER (Handles both food and drinks with various price structures) ---
   // Updated to consistently use 'pris' for single price check
    const renderMenuItem = (item, categoryKey) => {
     const priceStyle = "text-lg font-semibold text-stone-800 flex-shrink-0 ml-4"; let priceElement = null;

     // Handle various price structures
     // Prioritize 'price' from the API response
     if (item.price !== undefined && typeof item.price === 'number') { priceElement = <p className={priceStyle}>{formatPrice(item.price)}</p>; }
     // Fallback to old 'pris' if 'price' is not available (for compatibility with old data structure if needed)
     else if (item.pris !== undefined && typeof item.pris === 'number') { priceElement = <p className={priceStyle}>{formatPrice(item.pris)}</p>; }
     else if (item.pris_4cl && item.pris_6cl) { priceElement = <p className={priceStyle}>{formatPrice(item.pris_4cl)} / {formatPrice(item.pris_6cl)} <span className="text-xs font-normal">(4cl/6cl)</span></p>; }
     else if (item.pris_6cl) { priceElement = <p className={priceStyle}>{formatPrice(item.pris_6cl)} <span className="text-xs font-normal">(6cl)</span></p>; }
     else if (item.glas && item.flaska) { priceElement = <p className={priceStyle}>{formatPrice(item.glas)} / {formatPrice(item.flaska)} <span className="text-xs font-normal">(Glas/Flaska)</span></p>; }
     else if (item.flaska) { priceElement = <p className={priceStyle}>{formatPrice(item.flaska)} <span className="text-xs font-normal">(Flaska)</span></p>; }
     else if (Array.isArray(item.pris)) { priceElement = <p className={priceStyle}>{item.pris.map(formatPrice).join(' / ')}</p>; }
     else if (item.pris !== undefined) { // Handle non-numeric prices (like strings)
         priceElement = <p className={priceStyle}>{item.pris}</p>;
     }


     // Use 'name' from API response, fallback to 'namn'
     const itemName = item.name || item.namn;
     // Use 'description' from API response, fallback to 'ingredienser'
     const itemDescription = item.description || (item.ingredienser ? (Array.isArray(item.ingredienser) ? item.ingredienser.join(', ') : item.ingredienser) : null);

     // Use a unique key, combining name and id if available
     const itemKey = itemName + (item.id ? `-${item.id}` : '');

     return (
       <li key={itemKey} className="flex items-start justify-between space-x-4"> {/* Use items-start for top alignment, add space-x */}
         {item.image_path && item.id && (
           <img
             src={`https://grandeplatform.eu.pythonanywhere.com/static/images/menu_items/${item.id}_thumb.${item.image_path.split('.').pop()}`} // Construct thumbnail URL
             alt={itemName}
             className="w-16 h-16 rounded-md object-cover flex-shrink-0" // Tailwind classes for small image
           />
         )}
         <div className="flex-grow"> {/* Allow text content to take available space */}
           <h3 className="text-lg font-semibold text-stone-800">{itemName}</h3>
           {/* Display description */}
           {itemDescription && <p className="text-sm text-gray-600 italic mt-1">{itemDescription}</p>}
           {/* Display English description if available and different from Swedish */}
           {item.english && item.english !== item.description && <p className="text-xs text-gray-500 mt-1">({item.english})</p>}
           {/* Display volume/alcohol */}
           {(item.volume || item.alcohol_content) && (
             <p className="text-xs text-gray-500 mt-1">
               {item.volume}{item.volume && item.alcohol_content && ', '}{item.alcohol_content}
             </p>
           )}
         </div>
         {priceElement}
       </li>
     );
   };

   // --- Other rendering helpers (updated to use 'price' and 'description') ---
   // These helpers might become redundant if the API structure is consistent,
   // but keeping them for now and adjusting to use 'price' and 'description'
   const renderWineItem = (wineName, item) => {
     const priceStyle = "text-lg font-semibold text-stone-800 flex-shrink-0 ml-4"; let priceElement = null;
     // Prioritize 'price' from API
     if (item.price !== undefined && typeof item.price === 'number') { priceElement = <p className={priceStyle}>{formatPrice(item.price)}</p>; }
     else if (item.glas && item.flaska) { priceElement = <p className={priceStyle}>{formatPrice(item.glas)} / {formatPrice(item.flaska)} <span className="text-xs font-normal">(Glas/Flaska)</span></p>; }
     else if (item.flaska) { priceElement = <p className={priceStyle}>{formatPrice(item.flaska)} <span className="text-xs font-normal">(Flaska)</span></p>; }
     else if (item.pris) { priceElement = <p className={priceStyle}>{formatPrice(item.pris)}</p>; } // Fallback to 'pris'
     return ( <li key={wineName} className="flex justify-between items-start"> <div> <h3 className="text-lg font-semibold text-stone-800">{wineName}</h3> {item.typ && <p className="text-sm font-medium text-gray-700 mt-1">{item.typ}</p>} {item.description && <p className="text-sm text-gray-600 italic mt-1">{item.description}</p>} {item.beskrivning && !item.description && <p className="text-sm text-gray-600 italic mt-1">{item.beskrivning}</p>} </div> {priceElement} </li> );
    };
   const renderCoffeeItem = (coffeeName, price) => {
     const priceStyle = "text-lg font-semibold text-stone-800 flex-shrink-0 ml-4"; let priceText = '';
     // Assuming price here is directly the price value from the API or old data
     if (Array.isArray(price)) { priceText = price.map(formatPrice).join(' / '); } else { priceText = formatPrice(price); } // Already uses formatPrice
     return ( <li key={coffeeName} className="flex justify-between items-start"> <h3 className="text-lg font-semibold text-stone-800">{coffeeName}</h3> <p className={priceStyle}>{priceText}</p> </li> );
    };
    const renderTillbehorItem = (itemName, price) => {
        const priceStyle = "text-lg font-semibold text-stone-800 flex-shrink-0 ml-4";
        // Assuming price here is directly the price value from the API or old data
        return ( <li key={itemName} className="flex justify-between items-start"> <h3 className="text-lg font-semibold text-stone-800">{itemName}</h3> <p className={priceStyle}>{formatPrice(price)}</p> </li> ); // Already uses formatPrice
    };
   const renderAvecSnaps = (categoryKey, items) => {
        // This structure seems specific to the old data.
        // If the API returns Avec/Snaps with an 'items' array like other categories,
        // this helper might need significant changes or become redundant.
        // For now, adapting to use 'price' if available in special items.
        return ( <section key={categoryKey} className="pt-0"> <div className="mb-4 space-y-1"> {items['4cl'] && <p><span className="font-semibold">Pris 4cl:</span> {formatPrice(items['4cl'])}</p>} {items['6cl'] && <p><span className="font-semibold">Pris 6cl:</span> {formatPrice(items['6cl'])}</p>} </div> {items.special && ( <div className="mb-4"> <h4 className="text-md font-semibold text-stone-700 mb-1">Special / Per cl:</h4> <ul className="list-disc list-inside text-sm space-y-1"> {Object.entries(items.special).map(([name, price]) => ( <li key={name}>{name}: {typeof price === 'number' ? `${price}:-` : price}</li> ))} </ul> </div> )} {items.sorter && ( <div> <h4 className="text-md font-semibold text-stone-700 mb-1">Tillgängliga Sorter (exempel):</h4> <p className="text-sm text-gray-600 italic">{items.sorter.join(', ')}</p> </div> )} </section> ); // Already uses formatPrice where applicable
    };

  // --- Function to render the content INSIDE the category modal ---
  // Reverted to use renderMenuItem for all list-like categories
  const renderCategoryModalContent = () => {
    if (!selectedCategory || !allMenuData || !allMenuData[selectedCategory]) {
      return <p>Kunde inte ladda menyn för {selectedCategory}.</p>;
    }

    const categoryData = allMenuData[selectedCategory];

    // Check if the category data has an 'items' array (based on API structure)
    if (categoryData && Array.isArray(categoryData.items)) {
      return <ul className="space-y-5">{categoryData.items.map(item => renderMenuItem(item, selectedCategory))}</ul>;
    }

    // Fallback for old data structures if necessary (though ideally the API provides a consistent format)
    // This part might need refinement based on how the API actually structures non-list categories like Vin, Kaffe, Avec/Snaps
    // For now, keeping the old logic but it might not work correctly with the new API data if the structure is different.
    console.warn("Unexpected category data structure for:", selectedCategory, categoryData);

    // Attempt to render using old helpers if the new structure isn't found
    if (selectedCategory === "Vin" && typeof categoryData === 'object' && !Array.isArray(categoryData)) {
         return <ul className="space-y-5">{Object.entries(categoryData).map(([wineName, itemData]) => renderWineItem(wineName, itemData))}</ul>;
    } else if (selectedCategory === "Kaffe" && typeof categoryData === 'object' && !Array.isArray(categoryData)) {
         return <ul className="space-y-5">{Object.entries(categoryData).map(([coffeeName, price]) => renderCoffeeItem(coffeeName, price))}</ul>;
    } else if (selectedCategory === "Tillbehör" && typeof categoryData === 'object' && !Array.isArray(categoryData)) {
         return <ul className="space-y-5">{Object.entries(categoryData).map(([name, price]) => renderTillbehorItem(name, price))}</ul>;
    } else if (selectedCategory === "Avec / Snaps" && typeof categoryData === 'object' && !Array.isArray(categoryData)) {
         return renderAvecSnaps(selectedCategory, categoryData);
    }


    return <p>Visningsformat ej definierat eller data saknas för {selectedCategory}.</p>;
  };


  // --- Main Render Function for Menu Page ---
  return (
    <div className="container mx-auto px-6 py-10 md:px-12 md:py-14 font-sans animate-fade-in">
      {/* Back Button */}
       <button
        onClick={() => navigateTo('home')}
        className="mb-6 inline-flex items-center text-red-700 hover:text-red-900 transition duration-150 group"
      >
        <svg className="w-5 h-5 mr-2 transform group-hover:-translate-x-1 transition-transform duration-150" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path></svg>
        Tillbaka till Hem
      </button>

      <h1 className="text-4xl md:text-5xl font-serif font-bold text-center text-red-800 mb-8 md:mb-10">
        Vår Italienska Meny i Eslöv
      </h1>
      <p className="text-center text-gray-700 mb-8 max-w-2xl mx-auto">
        Utforska vår autentiska italienska meny på Grande Italiensk Restaurang i Eslöv. Vi erbjuder ett brett utbud av klassiska rätter, från läckra pizzor och pastarätter till antipasti och secondi piatti. Välkommen att uppleva en bit av Italien hos oss!
      </p>

      {/* Menu Toggle Buttons */}
      <div className="flex justify-center space-x-4 mb-10">
        <button
          onClick={() => setActiveMenuSection('food')}
          className={`py-2 px-6 rounded-lg font-semibold transition duration-200 ease-in-out ${
            activeMenuSection === 'food'
              ? 'bg-red-700 text-white shadow-md'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Matmeny
        </button>
        <button
          onClick={() => setActiveMenuSection('drinks')}
          className={`py-2 px-6 rounded-lg font-semibold transition duration-200 ease-in-out ${
            activeMenuSection === 'drinks'
              ? 'bg-red-700 text-white shadow-md'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Dryckesmeny
        </button>
      </div>

      {/* Render Loading/Error or Grid of Category Cards */}
      {loading && <p className="text-center text-gray-600">Laddar meny...</p>}
      {error && <p className="text-center text-red-600">Kunde inte ladda menyn: {error.message}</p>}
      {menuData && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {/* Filter categories based on activeMenuSection */}
          {/* Filter and reorder categories based on activeMenuSection */}
          {(() => {
            if (!menuData) return [];

            const categoriesToMove = ["Barn meny", "Dolci (desserter)", "Tillbehör"];
            const foodKeywords = ["antipasti", "pasta", "insalate", "secondi piatti", "italiensk sandwich", "barn meny", "dolci", "tillbehör", "pizzor"];
            const drinkKeywords = ["avec", "snaps", "cider", "drinkar", "fatöl", "flaska", "kaffe", "läsk", "vatten", "lätt", "alkoholfritt", "vin"];

            const filteredCategories = Object.entries(menuData)
              .filter(([categoryName, categoryData]) => {
                const isFood = foodKeywords.some(keyword => categoryName.toLowerCase().includes(keyword));
                const isDrink = drinkKeywords.some(keyword => categoryName.toLowerCase().includes(keyword));

                if (activeMenuSection === 'food') {
                  return isFood && !isDrink;
                } else { // activeMenuSection === 'drinks'
                  return isDrink || !isFood;
                }
              });

            // Separate categories to move to the end
            const categoriesAtEnd = filteredCategories.filter(([categoryName]) =>
              categoriesToMove.includes(categoryName)
            );
            const remainingCategories = filteredCategories.filter(([categoryName]) =>
              !categoriesToMove.includes(categoryName)
            );

            // Sort remaining categories alphabetically
            remainingCategories.sort(([nameA], [nameB]) => nameA.localeCompare(nameB));

            // Concatenate to get the final order
            const orderedCategories = [...remainingCategories, ...categoriesAtEnd];

            return orderedCategories.map(([categoryName, categoryData]) => (
              <MenuCategoryCard
                key={categoryName}
                categoryName={categoryName}
                onClick={() => openCategoryModal(categoryName)}
              />
            ));
          })()}
        </div>
      )}

        {/* Modal for displaying items of the selected category */}
        <Modal
            isOpen={selectedCategory !== null}
            onClose={closeCategoryModal}
            title={selectedCategory || ''} // Use selected category name as title
        >
            {/* Render the content based on the selected category */}
            {selectedCategory && renderCategoryModalContent()}
        </Modal>


       <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in { animation: fadeIn 0.5s ease-out forwards; }
      `}</style>
    </div>
  );
};

export default MenuPage;